import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logAdminAction } from "@/lib/adminAuth";
import { ArrowLeft, Eye, GitCompare, RotateCcw, Download, Circle } from "lucide-react";
import { format } from "date-fns";
import { safeUUID } from "@/lib/utils";

interface Version {
  id: string;
  version_number: number;
  template_snapshot: any;
  change_summary: string;
  changed_by: string;
  created_at: string;
  changed_by_profile?: { full_name: string };
}

export default function VersionHistory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [template, setTemplate] = useState<any>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [compareVersions, setCompareVersions] = useState<[Version | null, Version | null]>([null, null]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Validate UUID before loading
    if (id && !safeUUID(id)) {
      console.error(`Invalid template ID: "${id}"`);
      toast({
        title: "Invalid Template ID",
        description: "The template ID is invalid.",
        variant: "destructive",
      });
      navigate('/admin/templates');
      return;
    }
    loadTemplate();
    loadVersionHistory();
  }, [id]);

  async function loadTemplate() {
    try {
      const { data, error } = await supabase
        .from('document_templates')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setTemplate(data);
    } catch (error: any) {
      console.error('Error loading template:', error);
      toast({
        title: "Error",
        description: "Failed to load template",
        variant: "destructive",
      });
    }
  }

  async function loadVersionHistory() {
    try {
      const { data, error } = await supabase
        .from('template_version_history')
        .select('*')
        .eq('template_id', id)
        .order('version_number', { ascending: false });

      if (error) throw error;

      // Fetch changed_by profiles
      const versionsWithProfiles = await Promise.all(
        (data || []).map(async (version) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('id', version.changed_by)
            .single();

          return {
            ...version,
            changed_by_profile: profile || { full_name: 'Unknown' }
          };
        })
      );

      setVersions(versionsWithProfiles);
    } catch (error: any) {
      console.error('Error loading version history:', error);
      toast({
        title: "Error",
        description: "Failed to load version history",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  function handleViewVersion(version: Version) {
    setSelectedVersion(version);
    setViewModalOpen(true);
  }

  function handleCompareVersions(version: Version) {
    if (!compareVersions[0]) {
      setCompareVersions([version, null]);
      toast({
        title: "Select Second Version",
        description: "Click another version to compare",
      });
    } else {
      setCompareVersions([compareVersions[0], version]);
      setCompareModalOpen(true);
    }
  }

  function cancelCompare() {
    setCompareVersions([null, null]);
  }

  async function handleRestoreVersion(version: Version) {
    if (!confirm(`Restore to version ${version.version_number}? This overwrites the live template.`)) {
      return;
    }

    try {
      const snapshot = (version.template_snapshot || {}) as Record<string, any>;
      const { id: _snapshotId, created_at: _createdAt, ...fields } = snapshot;

      const { error } = await supabase
        .from('document_templates')
        .update(fields)
        .eq('id', id);

      if (error) throw error;

      await logAdminAction('restored_template_version', 'document_template', id, {
        template_name: template?.template_name,
        restore_to_version: version.version_number,
      });

      toast({
        title: "Version restored",
        description: `The template now matches version ${version.version_number}.`,
      });

      navigate(`/admin/templates/documents/${id}`);
    } catch (error: any) {
      console.error('Error restoring version:', error);
      toast({
        title: "Error",
        description: "Failed to restore this version",
        variant: "destructive",
      });
    }
  }

  if (loading) {
    return <div className="container mx-auto p-6">Loading version history...</div>;
  }

  const currentVersion = template?.current_version;

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/templates')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Version History</h1>
            <p className="text-muted-foreground">{template?.template_name}</p>
          </div>
        </div>
        {compareVersions[0] && !compareVersions[1] && (
          <Button variant="outline" onClick={cancelCompare}>
            Cancel Compare
          </Button>
        )}
      </div>

      {versions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No version history available</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Timeline */}
          <div className="relative">
            {versions.map((version, index) => {
              const isCurrent = version.version_number === currentVersion;
              const isCompareSelected = compareVersions[0]?.id === version.id || compareVersions[1]?.id === version.id;

              return (
                <div key={version.id} className="relative pl-8 pb-8">
                  {/* Timeline line */}
                  {index < versions.length - 1 && (
                    <div className="absolute left-[11px] top-6 bottom-0 w-0.5 bg-border" />
                  )}

                  {/* Timeline dot */}
                  <div className="absolute left-0 top-2">
                    <Circle
                      className={`h-6 w-6 ${
                        isCurrent
                          ? 'fill-primary text-primary'
                          : 'fill-muted text-muted-foreground'
                      }`}
                    />
                  </div>

                  {/* Version Card */}
                  <Card className={isCompareSelected ? 'ring-2 ring-primary' : ''}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold">Version {version.version_number}</h3>
                            {isCurrent && (
                              <Badge variant="default">Current</Badge>
                            )}
                            {isCompareSelected && (
                              <Badge variant="outline">Selected for Compare</Badge>
                            )}
                          </div>

                          <p className="text-sm text-muted-foreground">
                            {version.change_summary || 'No summary available'}
                          </p>

                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>By {version.changed_by_profile?.full_name}</span>
                            <span>•</span>
                            <span>{format(new Date(version.created_at), 'PPp')}</span>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewVersion(version)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCompareVersions(version)}
                            disabled={compareVersions[1] !== null}
                          >
                            <GitCompare className="h-3 w-3 mr-1" />
                            Compare
                          </Button>

                          {!isCurrent && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRestoreVersion(version)}
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Restore
                            </Button>
                          )}

                          <Button
                            variant="outline"
                            size="sm"
                            disabled
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* View Version Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              View Version {selectedVersion?.version_number}
            </DialogTitle>
          </DialogHeader>
          {selectedVersion && (
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Template Details</h4>
                <div className="border rounded-lg p-4 bg-muted/50">
                  <pre className="text-xs overflow-auto max-h-[500px]">
                    {JSON.stringify(selectedVersion.template_snapshot, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Compare Versions Modal */}
      <Dialog open={compareModalOpen} onOpenChange={setCompareModalOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Compare Versions {compareVersions[0]?.version_number} and {compareVersions[1]?.version_number}
            </DialogTitle>
          </DialogHeader>
          {compareVersions[0] && compareVersions[1] && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium mb-2">
                  Version {compareVersions[0].version_number}
                </h4>
                <div className="border rounded-lg p-4 bg-muted/50">
                  <pre className="text-xs overflow-auto max-h-[500px]">
                    {JSON.stringify(compareVersions[0].template_snapshot, null, 2)}
                  </pre>
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">
                  Version {compareVersions[1].version_number}
                </h4>
                <div className="border rounded-lg p-4 bg-muted/50">
                  <pre className="text-xs overflow-auto max-h-[500px]">
                    {JSON.stringify(compareVersions[1].template_snapshot, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
